import React, { useState, useEffect } from "react";
import { FiPlus, FiX } from "react-icons/fi";
import { BASE_URL } from "../../../utils/constant";
import OrganisationAccessScreen from "./IndividualAccess";
import {
  MdCheckBox,
  MdCheckBoxOutlineBlank,
  MdOutlinePersonOutline,
  MdOutlinePersonRemoveAlt1,
} from "react-icons/md";
// Import your alert system - adjust path as needed
import { useAlert } from "components/UI_components/Alert";
import { useNavigate } from "react-router-dom";
import AccountModal from "../../../components/AccountModal";
import OrganizationEmptyState from "components/UI_components/OrganizationEmptyState";
import { SubscriptionAPI } from "api/dashboard/service";
import LoadingScreen from "components/UI_components/LoadingScreen";

const PERMISSION_COLUMNS = [
  { key: "is_admin", label: "Admin" },
  { key: "procurement_control", label: "Procurement" },
  { key: "finance_control", label: "Finance" },
  { key: "vendor_control", label: "Vendor" },
  { key: "contractor_control", label: "Contractor" },
  { key: "design_admin", label: "Design Admin" },
  { key: "design_management", label: "Manage Design" },
  // { key: "lead_approver", label: "Lead Admin" },
  // { key: "lead_control", label: "Manage Lead" },
  { key: "snag_admin", label: "Snag Admin" },
];

const PermissionIndicator = ({ checked }) =>
  checked ? (
    <MdCheckBox className="h-5 w-5 text-btn-bg" />
  ) : (
    <MdCheckBoxOutlineBlank className="h-5 w-5 text-[#C6BCD0]" />
  );

export default function OrganizationTeam() {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [teamMembers, setTeamMembers] = useState(
    JSON.parse(localStorage.getItem("orgMembers")) || [],
  );
  const [showAddMember, setShowAddMember] = useState(false);
  const [inviteInput, setInviteInput] = useState("");
  const [inviteEmails, setInviteEmails] = useState([]);
  const [inviteError, setInviteError] = useState("");
  const [isInviting, setIsInviting] = useState(false);
  const [inviteProgress, setInviteProgress] = useState({ current: 0, total: 0 });
  const [selectedMember, setSelectedMember] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [pageLoading, setPageLoading] = useState(false);
  const [, setLoading] = useState(false);
  const [userData, setUserData] = useState(
    JSON.parse(localStorage.getItem("user")),
  );
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [currentMember, setCurrentMember] = useState(null);
  const navigate = useNavigate();

  const organizationId = JSON.parse(localStorage.getItem("organization"))?.id;

  const normalizeEmail = (email) => email.trim().toLowerCase();

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const parseEmailInput = (value) =>
    value
      .split(/[,\n\r\t]+/)
      .map((email) => normalizeEmail(email))
      .filter(Boolean);

  const resetInviteModal = () => {
    setShowAddMember(false);
    setInviteInput("");
    setInviteEmails([]);
    setInviteError("");
    setIsInviting(false);
    setInviteProgress({ current: 0, total: 0 });
  };

  const addEmailsFromText = (value) => {
    const parsedEmails = parseEmailInput(value);
    if (parsedEmails.length === 0) return;

    const validEmails = [];
    const invalidEmails = [];

    parsedEmails.forEach((email) => {
      if (isValidEmail(email)) {
        validEmails.push(email);
      } else {
        invalidEmails.push(email);
      }
    });

    if (validEmails.length > 0) {
      setInviteEmails((prevEmails) => {
        const existing = new Set(prevEmails.map(normalizeEmail));
        const nextEmails = [...prevEmails];

        validEmails.forEach((email) => {
          if (!existing.has(email)) {
            nextEmails.push(email);
            existing.add(email);
          }
        });

        return nextEmails;
      });
    }

    setInviteInput(invalidEmails.join(", "));
    setInviteError(
      invalidEmails.length
        ? `Invalid email address${invalidEmails.length > 1 ? "es" : ""}: ${invalidEmails.join(", ")}`
        : "",
    );
  };

  const removeInviteEmail = (emailToRemove) => {
    setInviteEmails((prevEmails) =>
      prevEmails.filter((email) => email !== emailToRemove),
    );
  };

  useEffect(() => {
    // Get the current user's email from localStorage
    const userEmail = JSON.parse(localStorage.getItem("user"))?.user_email;
    if (userEmail && teamMembers.length > 0) {
      const user = teamMembers.find(
        (member) => member.user?.email === userEmail,
      );
      // console.log("Current user:", user);
      if (user) {
        setCurrentMember(user);
      }
    } else {
      console.error("User email not found in localStorage");
    }
  }, [teamMembers]);

  // Uncomment if you have the alert system
  // const { addAlert } = useAlert();

  // Fallback alert function if useAlert is not available
  const { addAlert } = useAlert();
  // Get user details
  async function getUserRole() {
    try {
      const accessToken = localStorage.getItem("access");

      if (accessToken) {
        const response = await fetch(`${BASE_URL}/auth/user-details-of-user/`, {
          method: "GET",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          }),
        });

        const data = await response.json();
        if (data?.status) {
          setUserData(data?.data);
        } else {
          return;
        }
      }
    } catch (error) {
      addAlert(error?.message || "Failed to get user details", "error");
    }
  }

  // Fetch all users for adding members
  const fetchAllUsers = async () => {
    try {
      const accessToken = localStorage.getItem("access");
      if (!accessToken) {
        addAlert("Access token not found", "error");
        return;
      }

      const response = await fetch(`${BASE_URL}/auth/search-users/`, {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        }),
      });

      const result = await response.json();
      if (!result?.status) {
        addAlert(result?.message || "Failed to fetch users", "error");
        return;
      }

      setAllUsers(result?.data);
    } catch (error) {
      addAlert("Failed to fetch users", "error");
    }
  };

  // Fetch organization members
  const fetchMembers = async () => {
    if (teamMembers.length === 0) {
      setPageLoading(true);
    }
    try {
      const accessToken = localStorage.getItem("access");
      if (!accessToken || !organizationId) return;

      const response = await fetch(
        `${BASE_URL}/org/organization/${organizationId}/members/`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      if (!response.ok) throw new Error("Failed to fetch organization members");

      const data = await response.json();
      if (data.status && Array.isArray(data.data)) {
        setTeamMembers(data.data);
        localStorage.setItem("orgMembers", JSON.stringify(data.data));
      } else {
        setTeamMembers([]);
      }
    } catch (err) {
      console.error("Error fetching organization members:", err);
      setTeamMembers([]);
      addAlert("Failed to fetch organization members", "error");
    } finally {
      setPageLoading(false);
    }
  };

  const hasActiveSubscriptionForInvites = async () => {
    try {
      const subscriptionResponse =
        await SubscriptionAPI.checkUserSubscription(organizationId);

      return (
        subscriptionResponse?.status &&
        subscriptionResponse?.data?.active_flag &&
        subscriptionResponse?.data?.valid_till > new Date().toISOString()
      );
    } catch (e) {
      return false;
    }
  };

  const createMemberPayload = (user) => ({
    organization: organizationId,
    user_id: user?.user_id,
    is_admin: false,
    member_control: false,
    joined_as: "member",
    created_by: userData?.first_name,
    last_updated_by: userData?.first_name,
  });

  const submitMemberInvite = async (user, accessToken) => {
    const response = await fetch(
      `${BASE_URL}/org/organization/${organizationId}/add-member/`,
      {
        method: "POST",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        }),
        body: JSON.stringify(createMemberPayload(user)),
      },
    );

    return response.json();
  };

  const handleSubmitInvites = async () => {
    const typedEmails = parseEmailInput(inviteInput);
    const allInviteEmails = Array.from(
      new Set([...inviteEmails, ...typedEmails].map(normalizeEmail)),
    );

    const invalidEmails = allInviteEmails.filter((email) => !isValidEmail(email));
    if (invalidEmails.length > 0) {
      setInviteError(
        `Invalid email address${invalidEmails.length > 1 ? "es" : ""}: ${invalidEmails.join(", ")}`,
      );
      return;
    }

    if (allInviteEmails.length === 0) {
      setInviteError("Enter at least one email address.");
      return;
    }

    const accessToken = localStorage.getItem("access");
    if (!accessToken) {
      addAlert("Access token not found", "error");
      return;
    }

    const existingMemberEmails = new Set(
      teamMembers.map((member) => normalizeEmail(member?.user?.email || "")),
    );
    const duplicateMemberEmails = allInviteEmails.filter((email) =>
      existingMemberEmails.has(email),
    );
    const emailsToInvite = allInviteEmails.filter(
      (email) => !existingMemberEmails.has(email),
    );

    if (emailsToInvite.length === 0) {
      setInviteError("All entered users are already organization members.");
      return;
    }

    const usersByEmail = new Map(
      allUsers
        .filter((user) => user?.user_email)
        .map((user) => [normalizeEmail(user.user_email), user]),
    );
    const usersToInvite = emailsToInvite
      .map((email) => usersByEmail.get(email))
      .filter(Boolean);
    const unresolvedEmails = emailsToInvite.filter(
      (email) => !usersByEmail.has(email),
    );

    if (usersToInvite.length === 0) {
      setInviteError(
        `No registered users found for: ${unresolvedEmails.join(", ")}`,
      );
      return;
    }

    setInviteError("");
    setIsInviting(true);
    setInviteProgress({ current: 0, total: usersToInvite.length });

    const canInvite = await hasActiveSubscriptionForInvites();
    if (!canInvite) {
      addAlert(
        "No active subscription found. Please renew or purchase a plan to add members.",
        "error",
      );
      setIsInviting(false);
      setInviteProgress({ current: 0, total: 0 });
      return;
    }

    const failedEmails = [];
    let successCount = 0;

    for (let i = 0; i < usersToInvite.length; i++) {
      const user = usersToInvite[i];
      setInviteProgress({ current: i + 1, total: usersToInvite.length });

      try {
        const result = await submitMemberInvite(user, accessToken);
        if (result?.status) {
          successCount += 1;
        } else {
          failedEmails.push({
            email: normalizeEmail(user.user_email),
            message: result?.message || "Failed to invite",
          });
        }
      } catch (error) {
        failedEmails.push({
          email: normalizeEmail(user.user_email),
          message: "Network error",
        });
      }
    }

    setIsInviting(false);
    setInviteProgress({ current: 0, total: 0 });

    if (successCount > 0) {
      await fetchMembers();
      addAlert(
        `${successCount} member${successCount !== 1 ? "s" : ""} invited successfully`,
        "success",
      );
    }

    if (duplicateMemberEmails.length > 0) {
      addAlert(
        `${duplicateMemberEmails.length} email${duplicateMemberEmails.length !== 1 ? "s were" : " was"} already in the organization`,
        "info",
      );
    }

    if (unresolvedEmails.length > 0 || failedEmails.length > 0) {
      const remainingEmails = [
        ...unresolvedEmails,
        ...failedEmails.map((failure) => failure.email),
      ];
      setInviteEmails(remainingEmails);
      setInviteInput("");

      const failedMessage =
        failedEmails.length > 0
          ? ` Failed: ${failedEmails
              .map((failure) => `${failure.email} (${failure.message})`)
              .join(", ")}`
          : "";
      const unresolvedMessage =
        unresolvedEmails.length > 0
          ? ` No registered users found for: ${unresolvedEmails.join(", ")}`
          : "";

      setInviteError(`${unresolvedMessage}${failedMessage}`.trim());
      return;
    }

    resetInviteModal();
  };

  // Add member to organization
  // eslint-disable-next-line no-unused-vars
  const addMemberToOrganization = async (user) => {
    try {
      setLoading(true);
      const accessToken = localStorage.getItem("access");
      if (!accessToken) {
        addAlert("Access token not found", "error");
        setLoading(false);
        return;
      }
      if (teamMembers.some((m) => m.user?.email === user?.user_email)) {
        addAlert("User is already a member of the organization", "error");
        setLoading(false);
        return;
      }

      // Check active subscription before adding member
      try {
        const subscriptionResponse =
          await SubscriptionAPI.checkUserSubscription(organizationId);
        const hasActiveSubscription =
          subscriptionResponse?.status &&
          subscriptionResponse?.data?.active_flag &&
          subscriptionResponse?.data?.valid_till > new Date().toISOString();

        if (!hasActiveSubscription) {
          addAlert(
            "No active subscription found. Please renew or purchase a plan to add members.",
            "error",
          );
          setLoading(false);
          return;
        }
      } catch (e) {
        // If subscription check fails, block adding member
        addAlert("You don’t have a subscription to add members", "error");
        setLoading(false);
        return;
      }

      const response = await fetch(
        `${BASE_URL}/org/organization/${organizationId}/add-member/`,
        {
          method: "POST",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          }),
          body: JSON.stringify({
            organization: organizationId,
            user_id: user?.user_id,
            is_admin: false,
            member_control: false,
            joined_as: "member",
            created_by: userData?.first_name,
            last_updated_by: userData?.first_name,
          }),
        },
      );

      const result = await response.json();
      if (!result?.status) {
        if (result?.error === "limit_reached") {
          addAlert(
            result?.message ||
              "You have reached the limit for adding members in your current subscription plan",
            "error",
          );
        } else if (result?.message?.includes("owner")) {
          addAlert(
            result?.message || "Cannot add an owner of organisation",
            "error",
          );
        } else {
          addAlert(result?.message || "Failed to add member", "error");
        }
        setLoading(false);
        return;
      }

      await fetchMembers(); // Refresh members list
      setShowAddMember(false);
      setInviteInput("");
      setInviteEmails([]);
      setInviteError("");
      addAlert("Member added successfully", "success");
      setLoading(false);
    } catch (error) {
      setLoading(false);
      addAlert("Cannot add an owner of organisation", "error");
    }
  };

  // Handle delete click
  const handleDeleteClick = (member) => {
    setMemberToDelete(member);
    setShowConfirmation(true);
  };

  // Cancel delete operation
  const cancelDelete = () => {
    setShowConfirmation(false);
    setMemberToDelete(null);
  };

  // Confirm and execute member deletion
  const confirmDelete = async () => {
    if (!memberToDelete) return;

    setLoading(true);
    try {
      const accessToken = localStorage.getItem("access");
      if (!accessToken) {
        addAlert("Access token not found", "error");
        setLoading(false);
        setShowConfirmation(false);
        return;
      }

      const response = await fetch(
        `${BASE_URL}/org/organization/member/${memberToDelete.id}/delete/`,
        {
          method: "DELETE",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          }),
        },
      );

      if (response.ok) {
        addAlert("Member removed successfully", "success");
        await fetchMembers(); // Refresh the members list
      } else {
        const errorData = await response.json();
        addAlert(errorData?.message || "Failed to remove member", "error");
      }
    } catch (error) {
      addAlert("Error removing member", "error");
      console.error("Error removing member:", error);
    } finally {
      setLoading(false);
      setShowConfirmation(false);
      setMemberToDelete(null);
    }
  };

  const handleLogout = async () => {
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        refresh: localStorage.getItem("refresh"),
      }),
    };
    try {
      const response = await fetch(
        `${BASE_URL}/token/blacklist/`,
        requestOptions,
      );
      const data = await response.json();
      if (data?.code !== "token_not_valid") {
        localStorage.clear();
        navigate("/login");
      } else {
        console.log("Token not valid");
        localStorage.clear();
        navigate("/login");
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getUserDetails = async () => {
    const accessToken = localStorage.getItem("access");
    if (accessToken) {
      const response = await fetch(`${BASE_URL}/auth/user-details-of-user/`, {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        }),
      });
      const data = await response.json();
      if (data?.status) {
        setUserData(data?.data);
      }
    }
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  // This screen intentionally loads its initial data once on mount.
  useEffect(() => {
    getUserRole();
    fetchMembers();
    fetchAllUsers();
    getUserDetails();
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  const canViewPermissionsMatrix =
    currentMember?.joined_as === "owner" || currentMember?.is_admin;

  const canManageMembers =
    currentMember?.joined_as === "owner" ||
    currentMember?.is_admin ||
    currentMember?.member_control;

  const canAddMembers =
    currentMember?.user?.profession === "owner" ||
    currentMember?.joined_as === "owner";

  const canManageSpecificMember = (member) =>
    member?.joined_as !== "owner" && member?.user?.profession !== "owner";

  const canRemoveSpecificMember = (member) =>
    canManageSpecificMember(member) &&
    member?.user?.id !== currentMember?.user?.id;

  if (!organizationId) {
    return <OrganizationEmptyState />;
  }

  return (
    <div className="px-10 pt-8 pb-2">
      {/* Loading overlay */}

      {/* Add Member Modal */}
      {showAddMember && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-transparent px-4">
          <div className="w-full max-w-4xl overflow-hidden rounded-lg bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-[#E7E1EC] px-6 py-4">
              <h2 className="text-2xl font-semibold text-gray-900">
                Invite users
              </h2>
              <button
                type="button"
                className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-800 disabled:opacity-50"
                onClick={resetInviteModal}
                disabled={isInviting}
                aria-label="Close invite users"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-8">
              <label className="mb-3 block text-sm font-semibold text-[#2D2832]">
                Enter multiple email addresses
                <span className="text-red-500">*</span>
              </label>

              <div className="min-h-[180px] rounded-lg border border-[#D9DDE5] bg-white px-4 py-3 shadow-sm focus-within:border-btn-bg focus-within:ring-1 focus-within:ring-btn-bg">
                <div className="flex flex-wrap items-start gap-2">
                  {inviteEmails.map((email) => (
                    <span
                      key={email}
                      className="inline-flex max-w-full items-center gap-2 rounded-md border border-[#D9DDE5] bg-[#FAF7FC] px-3 py-2 text-sm font-medium text-[#2D2832]"
                    >
                      <span className="truncate">{email}</span>
                      <button
                        type="button"
                        className="text-red-500 hover:text-red-700 disabled:opacity-50"
                        onClick={() => removeInviteEmail(email)}
                        disabled={isInviting}
                        aria-label={`Remove ${email}`}
                      >
                        <FiX className="h-4 w-4" />
                      </button>
                    </span>
                  ))}

                  <textarea
                    className="min-h-[40px] flex-1 resize-none border-none bg-transparent p-2 text-sm text-gray-700 outline-none placeholder:text-gray-400"
                    placeholder="Enter email address"
                    value={inviteInput}
                    onChange={(event) => {
                      const value = event.target.value;
                      if (/[,\n\r\t]/.test(value)) {
                        addEmailsFromText(value);
                      } else {
                        setInviteInput(value);
                        if (inviteError) setInviteError("");
                      }
                    }}
                    onKeyDown={(event) => {
                      if (
                        event.key === "Enter" ||
                        event.key === "Tab" ||
                        event.key === ","
                      ) {
                        event.preventDefault();
                        addEmailsFromText(inviteInput);
                      }
                    }}
                    onBlur={() => addEmailsFromText(inviteInput)}
                    disabled={isInviting}
                    rows={1}
                    autoFocus
                  />
                </div>
              </div>

              <p className="mt-3 text-sm text-[#6D6475]">
                Note: Press Enter or Tab or use comma/newline to separate email
                ids.
              </p>

              {inviteError && (
                <p className="mt-3 text-sm font-medium text-red-600">
                  {inviteError}
                </p>
              )}
            </div>

            <div className="border-t border-[#E7E1EC] bg-[#FAF7FC] px-6 py-4">
              {isInviting && (
                <div className="mb-4">
                  <div className="mb-1 flex items-center justify-between text-sm text-[#6D6475]">
                    <span>Sending invitations...</span>
                    <span>
                      {inviteProgress.current} of {inviteProgress.total}
                    </span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-[#E7E1EC]">
                    <div
                      className="h-full rounded-full bg-btn-bg transition-all duration-300"
                      style={{
                        width:
                          inviteProgress.total > 0
                            ? `${(inviteProgress.current / inviteProgress.total) * 100}%`
                            : "0%",
                      }}
                    />
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  className="rounded-md px-5 py-2 text-sm font-medium text-[#6D6475] hover:bg-white disabled:opacity-50"
                  onClick={resetInviteModal}
                  disabled={isInviting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="rounded-md bg-btn-bg px-8 py-2 text-sm font-semibold text-white hover:bg-purple-900 disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={handleSubmitInvites}
                  disabled={
                    isInviting ||
                    (inviteEmails.length === 0 && inviteInput.trim().length === 0)
                  }
                >
                  {isInviting ? "Adding..." : "Add members"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmation && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">
              Confirm Member Removal
            </h3>
            <p className="mb-6">
              Are you sure you want to remove {memberToDelete?.user?.first_name}{" "}
              {memberToDelete?.user?.last_name} from this organization? This
              action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300 text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 rounded-md hover:bg-red-700 text-white"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        className={`transition-opacity duration-200 ${
          showAddMember ? "opacity-50" : "opacity-100"
        }`}
      >
        {!selectedMember ? (
          <>
            <LoadingScreen loading={pageLoading}>
            <div className="mb-2">
              <h1 className="text-2xl font-semibold text-gray-900">Organization Team</h1>
              <p className="text-sm text-muted-foreground">
                Manage your organization team members and their roles
              </p>
            </div>
            {/* Current User Card */}
            <div className="bg-white rounded-2xl p-6 flex items-center w-full">
              <div className="h-14 w-14 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-2xl overflow-hidden">
                {currentMember?.user?.userpic ? (
                  <img
                    src={currentMember?.user?.userpic}
                    alt={
                      currentMember?.user?.first_name +
                      " " +
                      currentMember?.user?.last_name
                    }
                    className="h-14 w-14 rounded-full object-cover"
                  />
                ) : (
                  `${currentMember?.user?.first_name?.[0] || ""}${
                    currentMember?.user?.last_name?.[0] || ""
                  }`
                )}
              </div>
              <div className="ml-6 flex-1">
                <div className="font-medium text-gray-800 text-lg">
                  {currentMember?.user?.first_name}{" "}
                  {currentMember?.user?.last_name} {"(You)"}
                  <span className="ml-2 text-xs items-center font-medium px-2 py-0.5 bg-[#F3F0F5] text-btn-bg rounded-full align-middle">
                    {currentMember?.user?.profession || "Member"}
                  </span>
                  {/* {currentMember?.user?.profession === "owner" && (
                    <span className="ml-2 text-xs items-center font-medium px-2 py-0.5 bg-[#F3F0F5] text-btn-bg rounded-full align-middle">
                      Super Admin
                    </span>
                  )} */}
                </div>
                <div className="text-gray-500 text-sm mt-0.5">
                  {currentMember?.user?.email}
                </div>
              </div>
              {/* <button
                className="border-[1px] border-btn-bg bg-[#ECE6F0] text-btn-bg px-6 py-1.5 rounded-full hover:bg-purple-50 font-medium text-sm flex items-center gap-2 transition-all"
                onClick={() => setIsSettingsOpen(true)}
              >
                <FiSettings className="w-4 h-4 mr-1" /> Manage Account
              </button> */}
            </div>

            {/* Team Members Section */}
            <div className="bg-white rounded-2xl mt-4 w-full">
              <div className="flex items-center mx-2 justify-between px-3 py-3">
                <div>
                  <div className="font-normal text-[#737373]">
                    {`${teamMembers.length} organization member${
                      teamMembers.length !== 1 ? "s" : ""
                    }`}
                  </div>
                </div>
                <button
                  className="bg-btn-bg hover:bg-purple-900 text-white px-3 py-2 rounded-lg font-medium justify-between flex flex-row items-center"
                  onClick={() => setShowAddMember(true)}
                  style={{
                    rowGap: "12px",
                    display: canAddMembers ? "flex" : "none",
                  }}
                >
                  <div className="bg-[#58267F] rounded-full p-1 mr-2">
                    <FiPlus className="" />
                  </div>
                  New Member
                </button>
              </div>
              {canViewPermissionsMatrix ? (
                <div
                  className="h-[48vh] overflow-auto px-4 pb-4"
                  style={{
                    scrollbarWidth: "thin",
                  }}
                >
                  <div className="rounded-md overflow-x-auto border border-[#E7E1EC]">
                    <table className="w-full border-collapse">
                      <thead className="bg-[#FAF7FC]">
                        <tr className="border-b border-[#E7E1EC]">
                          <th className="sticky left-0 z-10 bg-[#FAF7FC] px-3 py-2 text-left text-xs font-semibold tracking-[0.08em] text-[#6D6475]">
                            Member
                          </th>
                          {PERMISSION_COLUMNS.map((column) => (
                            <th
                              key={column.key}
                              className="px-3 py-2 text-center text-[11px] font-semibold tracking-[0.08em] text-[#6D6475]"
                            >
                              {column.label}
                            </th>
                          ))}
                          <th className="sticky right-0 z-10 bg-[#FAF7FC] px-3 py-2 text-center text-xs font-semibold tracking-[0.08em] text-[#6D6475]">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white">
                        {teamMembers.map((member, index) => (
                          <tr
                            key={member.id}
                            className={
                              index !== teamMembers.length - 1
                                ? "border-b border-[#F0EBF4]"
                                : ""
                            }
                          >
                            <td className="sticky left-0 z-[1] bg-white px-3 py-3">
                              <div className="flex items-center gap-3">
                                <div className="h-11 w-11 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-bold text-sm overflow-hidden flex-shrink-0">
                                  {member.user?.userpic ? (
                                    <img
                                      src={member.user?.userpic}
                                      alt={
                                        member.user?.first_name +
                                        " " +
                                        member.user?.last_name
                                      }
                                      className="h-11 w-11 rounded-full object-cover"
                                    />
                                  ) : (
                                    `${member.user?.first_name?.[0] || ""}${
                                      member.user?.last_name?.[0] || ""
                                    }`
                                  )}
                                </div>
                                <div className="">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <p className="truncate text-sm font-semibold text-[#2D2832]">
                                      {member.user?.first_name}{" "}
                                      {member.user?.last_name}
                                      {member.user?.id ===
                                      currentMember?.user?.id
                                        ? " (You)"
                                        : ""}
                                    </p>
                                    <span className="rounded-full bg-[#F3F0F5] px-2 py-0.5 text-[11px] font-medium text-btn-bg">
                                      {member?.user?.profession || "Member"}
                                    </span>
                                    {member?.is_admin &&
                                      member?.user?.profession !== "owner" && (
                                        <span className="rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-800">
                                          Admin
                                        </span>
                                      )}
                                  </div>
                                  <p
                                    className="truncate text-xs text-[#7A7480]"
                                    title={member.user?.email}
                                  >
                                    {member.user?.email}
                                  </p>
                                </div>
                              </div>
                            </td>
                            {PERMISSION_COLUMNS.map((column) => (
                              <td
                                key={column.key}
                                className="px-3 py-3 text-center"
                              >
                                <div className="flex justify-center">
                                  <PermissionIndicator
                                    checked={Boolean(member?.[column.key])}
                                  />
                                </div>
                              </td>
                            ))}
                            <td className="sticky right-0 z-[1] bg-white px-3 py-3">
                              <div className="flex items-center justify-center gap-2">
                                {/* {canManageMembers &&
                                  canRemoveSpecificMember(member) && (
                                    <button
                                      className="inline-flex items-center gap-1 rounded-full border border-[#DD2C2C] px-3 py-1.5 text-xs font-semibold text-[#DD2C2C] transition-all hover:bg-red-50"
                                      onClick={() => handleDeleteClick(member)}
                                    >
                                      <MdOutlinePersonRemoveAlt1 className="h-4 w-4" />
                                      Remove
                                    </button>
                                  )} */}
                                {canManageMembers &&
                                  canManageSpecificMember(member) && (
                                    <button
                                      className="inline-flex items-center gap-1 rounded-full border border-btn-bg px-3 py-1.5 text-xs font-semibold text-btn-bg transition-all hover:bg-purple-50"
                                      onClick={() => setSelectedMember(member)}
                                    >
                                      <MdOutlinePersonOutline className="h-4 w-4" />
                                      Manage
                                    </button>
                                  )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div
                  className="h-[48vh] overflow-y-scroll"
                  style={{
                    scrollbarWidth: "none",
                  }}
                >
                  {teamMembers?.map((member, index) => (
                    <div
                      key={member.id}
                      className="flex items-center px-4 py-4"
                      style={{
                        borderBottom:
                          index !== teamMembers.length - 1
                            ? "0.5px solid #C3C3C3"
                            : "none",
                      }}
                    >
                      <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-bold text-lg overflow-hidden">
                        {member.user?.userpic ? (
                          <img
                            src={member.user?.userpic}
                            alt={
                              member.user?.first_name +
                              " " +
                              member.user?.last_name
                            }
                            className="h-12 w-12 rounded-full object-cover"
                          />
                        ) : (
                          `${member.user?.first_name?.[0] || ""}${
                            member.user?.last_name?.[0] || ""
                          }`
                        )}
                      </div>
                      <div className="ml-4 min-w-0 flex-1">
                        <div className="font-medium text-gray-800 text-base flex flex-row items-center">
                          {member.user?.first_name} {member.user?.last_name}
                          <span className="ml-2 text-xs font-medium px-2 py-0.5 bg-[#F3F0F5] text-btn-bg rounded-full">
                            {member?.user?.profession || "Member"}
                          </span>
                          {member?.is_admin &&
                            member?.user?.profession !== "owner" && (
                              <span className="ml-2 text-xs font-medium px-2 py-0.5 bg-green-100 text-green-800 rounded-full">
                                Admin
                              </span>
                            )}
                        </div>

                        <div
                          className="text-gray-500 text-sm mt-0.5"
                          style={{ maxWidth: "180px" }}
                          title={member.user?.email}
                        >
                          {member.user?.email}
                        </div>
                      </div>

                      <div
                        className="ml-auto flex gap-2 items-center"
                        style={{
                          display: canManageMembers ? "flex" : "none",
                        }}
                      >
                        <button
                          style={{
                            display: canRemoveSpecificMember(member)
                              ? "flex"
                              : "none",
                          }}
                          className="border-[1px] border-[#DD2C2C] text-[#DD2C2C] px-6 py-1.5 rounded-full hover:bg-red-50 font-semibold text-sm flex items-center gap-2 transition-all"
                          onClick={() => handleDeleteClick(member)}
                        >
                          <MdOutlinePersonRemoveAlt1 className="w-4 h-4 mr-1" />
                          Remove
                        </button>
                        <button
                          className="border-[1px] border-btn-bg text-btn-bg px-6 py-1.5 rounded-full hover:bg-purple-50 font-semibold text-sm flex items-center gap-2 transition-all"
                          onClick={() => setSelectedMember(member)}
                          style={{
                            display: canManageSpecificMember(member)
                              ? "flex"
                              : "none",
                          }}
                        >
                          <MdOutlinePersonOutline className="w-4 h-4 mr-1" />
                          Manage
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            </LoadingScreen>
          </>
        ) : (
          <OrganisationAccessScreen
            member={selectedMember}
            currentOrgMember={currentMember}
            onClose={() => {
              setSelectedMember(null);
              fetchMembers();
            }}
          />
        )}
      </div>
      {userData?.user && (
        <AccountModal
          isOpen={isSettingsOpen && userData?.user}
          onClose={() => setIsSettingsOpen(false)}
          userData={userData}
          handleLogOut={handleLogout}
          refetch={getUserDetails}
        />
      )}
    </div>
  );
}
